import { useEffect, useState } from 'react';
import { api, fetchFileUrl } from '../lib/api.js';
import { Modal, useToast, Field } from './ui.jsx';
import Icon from './Icon.jsx';
import { prettySize, fileIcon } from './DocumentUpload.jsx';
import { relativeTime } from '../lib/format.js';

const TONE = { approved: 'pill-success', rejected: 'pill-danger', pending: 'pill-warning' };

/** Opens a protected document inline. The blob URL is revoked on close. */
function Viewer({ doc, onClose }) {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let revoke;
    fetchFileUrl(`/api/documents/${doc.id}/file`)
      .then((u) => {
        revoke = u;
        setUrl(u);
      })
      .catch((e) => setError(e.message));
    return () => revoke && URL.revokeObjectURL(revoke);
  }, [doc.id]);

  return (
    <Modal title={doc.filename} onClose={onClose} wide>
      {error ? (
        <p className="field-error">{error}</p>
      ) : !url ? (
        <div className="skeleton" style={{ height: 380 }} />
      ) : doc.mime === 'application/pdf' ? (
        <iframe src={url} title={doc.filename} className="doc-viewer" />
      ) : (
        <img src={url} alt={doc.filename} className="doc-viewer" />
      )}
      <p className="tiny dim mt-1">
        {doc.mime} · {prettySize(doc.size)} · uploaded {relativeTime(doc.created_at)}
      </p>
    </Modal>
  );
}

/**
 * The reviewer's working surface: every uploaded credential with an inline
 * viewer and an approve/reject decision per document.
 */
export default function DocumentReview({ nurse, kinds, onChange }) {
  const toast = useToast();
  const [viewing, setViewing] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(0);

  const label = (kind) => kinds.find((k) => k.key === kind)?.label || kind;

  const review = async (doc, status, reviewNote) => {
    setBusy(doc.id);
    try {
      const updated = await api.post(`/api/admin/documents/${doc.id}/review`, { status, note: reviewNote });
      onChange(nurse.id, updated);
      setRejecting(null);
      setNote('');
      toast.success(status === 'approved' ? `${label(doc.kind)} approved.` : `${label(doc.kind)} rejected.`);
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(0);
    }
  };

  return (
    <div className="doc-review">
      {nurse.documents.length === 0 ? (
        <p className="small dim">No documents uploaded yet.</p>
      ) : (
        nurse.documents.map((doc) => (
          <div key={doc.id} className="doc-row" data-status={doc.status}>
            <span className="icon-tile icon-tile-neutral" style={{ width: 34, height: 34 }}>
              <Icon name={fileIcon(doc.mime)} size={16} />
            </span>
            <div className="grow" style={{ minWidth: 0 }}>
              <div className="row-tight" style={{ flexWrap: 'wrap' }}>
                <span className="bold small">{label(doc.kind)}</span>
                <span className={`pill ${TONE[doc.status]}`}>{doc.status}</span>
              </div>
              <div className="tiny dim truncate">
                {doc.filename} · {prettySize(doc.size)} · {relativeTime(doc.created_at)}
              </div>
              {doc.review_note && <div className="tiny mt-1" style={{ color: 'var(--danger)' }}>↳ {doc.review_note}</div>}
            </div>
            <div className="row-tight">
              <button className="btn btn-sm" onClick={() => setViewing(doc)}>
                <Icon name="eye" /> View
              </button>
              {doc.status !== 'approved' && (
                <button
                  className="btn btn-sm btn-success"
                  disabled={busy === doc.id}
                  onClick={() => review(doc, 'approved')}
                  aria-label={`Approve ${label(doc.kind)}`}
                >
                  <Icon name="check" />
                </button>
              )}
              {doc.status !== 'rejected' && (
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => setRejecting(doc)}
                  aria-label={`Reject ${label(doc.kind)}`}
                >
                  <Icon name="close" />
                </button>
              )}
            </div>
          </div>
        ))
      )}

      {nurse.missingRequired?.length > 0 && (
        <div className="banner banner-warning" style={{ marginBottom: 0, marginTop: 10 }}>
          <Icon name="warning" />
          <span className="grow small">Not uploaded yet: {nurse.missingRequired.map(label).join(', ')}.</span>
        </div>
      )}

      {viewing && <Viewer doc={viewing} onClose={() => setViewing(null)} />}

      {rejecting && (
        <Modal
          title={`Reject ${label(rejecting.kind)}?`}
          onClose={() => setRejecting(null)}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setRejecting(null)}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={() => review(rejecting, 'rejected', note)}>
                Reject document
              </button>
            </>
          }
        >
          <p className="muted small">
            The nurse is notified and can upload a replacement, which puts them back in this queue.
          </p>
          <Field label="What is wrong with it?" id="doc-note" hint="Shown to the nurse.">
            <textarea
              id="doc-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="The scan is cut off — the registration number is not readable."
            />
          </Field>
        </Modal>
      )}
    </div>
  );
}
